import React, { useState, useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import toast, { Toaster } from "react-hot-toast";
import axios from "../Util/axiosConfig";
import PureModal from "react-pure-modal";

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [emailModal, setEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: "", message: "" });
  const [smsModal, setSmsModal] = useState(false);
  const [smsMessage, setSmsMessage] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    fetchContacts();
  }, [currentPage, searchTerm]);

  const fetchAllContacts = async () => {
    try {
      setIsLoadingAll(true);
      const response = await axios.get("/v1/api/users/all-contacts");
      if (response.data.success) {
        setAllContacts(response.data.data);
        setSelectedContacts(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch all contacts");
    } finally {
      setIsLoadingAll(false);
    }
  };

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/v1/api/users/contacts?page=${currentPage}&limit=25&search=${searchTerm}`
      );

      if (response.data.success) {
        setContacts(response.data.data);
        setTotalPages(response.data.pages);
      }
    } catch (error) {
      toast.error("Failed to fetch contacts");
    } finally {
      setLoading(false);
    }
  };

  // Add this pagination component inside the Contacts component
  const Pagination = () => {
    const pageNumbers = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    // Always show first page
    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) pageNumbers.push("...");
    }

    // Add pages around current page
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    // Always show last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pageNumbers.push("...");
      pageNumbers.push(totalPages);
    }

    return (
      <nav aria-label="Page navigation" className="mt-3 mb-3">
        <ul className="pagination justify-content-center">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
          </li>
          {pageNumbers.map((number, index) => (
            <li
              key={index}
              className={`page-item ${number === currentPage ? "active" : ""} ${
                number === "..." ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => number !== "..." && handlePageChange(number)}
              >
                {number}
              </button>
            </li>
          ))}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    const uploadedFiles = [];

    try {
      for (let file of files) {
        const formData = new FormData();
        formData.append("certificate", file);

        const response = await axios.post(
          "/v1/api/upload/certificate",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        if (response.data.success) {
          uploadedFiles.push({
            name: file.name,
            url: response.data.data.secure_url,
          });
        }
      }

      setAttachments([...attachments, ...uploadedFiles]);
      toast.success(`${uploadedFiles.length} file(s) uploaded successfully`);
    } catch (error) {
      toast.error("Failed to upload files");
    } finally {
      setIsUploading(false);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSendEmail = async () => {
    try {
      setIsSendingEmail(true);
      const emails = selectedContacts.map((contact) => contact.email);
      await axios.post("/v1/api/users/send-email", {
        emails,
        ...emailForm,
        attachments,
      });
      toast.success(
        `Sending emails to ${emails.length} recipients. This may take a few minutes.`
      );
      setEmailModal(false);
      setSelectedContacts([]);
      setSelectAll(false);
      setEmailForm({ subject: "", message: "" });
      setAttachments([]);
    } catch (error) {
      toast.error("Failed to send emails");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSendSms = async () => {
    try {
      setIsSendingSms(true);
      const phones = selectedContacts
        .map((contact) => contact.phone)
        .filter(Boolean);
      await axios.post("/v1/api/users/send-sms", {
        phones,
        message: smsMessage,
      });
      toast.success(
        `Sending SMS to ${phones.length} recipients. This may take a few minutes.`
      );
      setSmsModal(false);
      setSelectedContacts([]);
      setSelectAll(false);
      setSmsMessage("");
    } catch (error) {
      toast.error("Failed to send SMS");
    } finally {
      setIsSendingSms(false);
    }
  };

  return (
    <React.Fragment>
      <HelmetProvider>
        <Helmet>
          <title>Contacts Management - UGONSA</title>
        </Helmet>
        <Toaster />

        <div className="container-fluid mt-3">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      {selectedContacts.length > 0 && (
                        <span className="text-muted">
                          Selected {selectedContacts.length}{" "}
                          {selectedContacts.length === 1
                            ? "contact"
                            : "contacts"}
                          {selectedContacts.length > contacts.length &&
                            " across all pages"}
                        </span>
                      )}
                    </div>
                    <div>
                      <button
                        className="btn btn-primary me-2"
                        onClick={() => setEmailModal(true)}
                        disabled={selectedContacts.length === 0}
                      >
                        Send Email ({selectedContacts.length})
                      </button>
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => setSmsModal(true)}
                        disabled={selectedContacts.length === 0}
                      >
                        Send SMS ({selectedContacts.length})
                      </button>
                    </div>
                  </div>

                  <div className="form-group mb-4">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search contacts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {loading ? (
                    <p>Loading...</p>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectAll}
                                    onChange={(e) => {
                                      setSelectAll(e.target.checked);
                                      if (e.target.checked) {
                                        if (allContacts.length === 0) {
                                          // If we haven't loaded all contacts yet, fetch them
                                          fetchAllContacts();
                                        } else {
                                          setSelectedContacts(allContacts);
                                        }
                                      } else {
                                        setSelectedContacts([]);
                                      }
                                    }}
                                  />
                                  <label className="form-check-label">
                                    {isLoadingAll ? "Loading..." : "Select All"}
                                  </label>
                                </div>
                              </th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Phone</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {contacts.map((contact) => (
                              <tr key={contact.uid}>
                                <td>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedContacts.some(
                                      (c) => c.uid === contact.uid
                                    )}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedContacts([
                                          ...selectedContacts,
                                          contact,
                                        ]);
                                      } else {
                                        setSelectedContacts(
                                          selectedContacts.filter(
                                            (c) => c.uid !== contact.uid
                                          )
                                        );
                                        setSelectAll(false);
                                      }
                                    }}
                                  />
                                </td>
                                <td>{`${contact.surname} ${contact.firstName}`}</td>
                                <td>{contact.email}</td>
                                <td>{contact.phone}</td>
                                <td>
                                  <span
                                    className={`badge bg-${
                                      contact.verification_status === "verified"
                                        ? "success"
                                        : "warning"
                                    }`}
                                  >
                                    {contact.verification_status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="card-footer bg-white">
                        {contacts && contacts.length > 0 && <Pagination />}
                      </div>
                      ;
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <PureModal
          header="Send Email"
          isOpen={emailModal}
          closeButton="X"
          closeButtonPosition="header"
          onClose={() => {
            setEmailModal(false);
            return true;
          }}
        >
          <div className="p-3">
            <div className="form-group mb-3">
              <label>Subject</label>
              <input
                type="text"
                className="form-control"
                value={emailForm.subject}
                onChange={(e) =>
                  setEmailForm({ ...emailForm, subject: e.target.value })
                }
              />
            </div>
            <div className="form-group mb-3">
              <label>Message</label>
              <textarea
                className="form-control"
                rows="5"
                value={emailForm.message}
                onChange={(e) =>
                  setEmailForm({ ...emailForm, message: e.target.value })
                }
              />
            </div>

            <div className="form-group mb-3">
              <label>Attachments (optional)</label>
              <input
                type="file"
                className="form-control"
                multiple
                onChange={(e) => handleFileUpload(e.target.files)}
                disabled={isUploading}
              />
              {isUploading && (
                <div className="text-muted mt-2">
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Uploading files...
                </div>
              )}
              {attachments.length > 0 && (
                <div className="mt-2">
                  <small className="text-muted">Attached files:</small>
                  <ul className="list-group mt-1">
                    {attachments.map((file, index) => (
                      <li
                        key={index}
                        className="list-group-item d-flex justify-content-between align-items-center py-1"
                      >
                        <small>{file.name}</small>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => removeAttachment(index)}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSendEmail}
              disabled={
                isSendingEmail ||
                isUploading ||
                !emailForm.subject ||
                !emailForm.message
              }
            >
              {isSendingEmail ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Sending...
                </>
              ) : (
                "Send Email"
              )}
            </button>
          </div>
        </PureModal>

        <PureModal
          header="Send SMS"
          isOpen={smsModal}
          closeButton="X"
          closeButtonPosition="header"
          onClose={() => {
            setSmsModal(false);
            return true;
          }}
        >
          <div className="p-3">
            <div className="form-group mb-3">
              <label>Message</label>
              <textarea
                className="form-control"
                rows="5"
                maxLength={459}
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
              />
              <small className="text-muted">
                {smsMessage.length}/459 characters (
                {Math.ceil(smsMessage.length / 153) || 1} SMS segment
                {Math.ceil(smsMessage.length / 153) > 1 ? "s" : ""})
              </small>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSendSms}
              disabled={isSendingSms || !smsMessage}
            >
              {isSendingSms ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Sending...
                </>
              ) : (
                "Send SMS"
              )}
            </button>
          </div>
        </PureModal>
      </HelmetProvider>
    </React.Fragment>
  );
};

export default Contacts;
